import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FieldWrapper, FormlyModule } from '@ngx-formly/core';

@Component({
  selector: 'formly-field-date-control',
  template: `
  <h3 class="card-header">{{ props.label }} <mat-icon *ngIf="to['tooltip']"
                  matTooltip="{{ to['tooltip'] }}"
                  class="tooltip-icon"
                  matTooltipPosition="right"
                  >help</mat-icon
                ></h3>
  <form [formGroup]="dateForm" class="border p-4 rounded shadow bg-white">
      <div class="mb-3">
        <label for="fromDate" class="form-label">From Date:</label>
        <input type="date" class="form-control" [disabled]="getDisable()" id="fromDate" formControlName="fromDate" [formlyAttributes]="field">
      </div>
      <div class="mb-3">
        <label for="toDate" class="form-label">To Date:</label>
        <input type="date" class="form-control" [disabled]="getDisable()" id="toDate" formControlName="toDate" [min]="minToDate" [max]="maxToDate" [formlyAttributes]="field">
      </div>
      <ng-container #fieldComponent>    </ng-container>
    </form>
  `,
  standalone:true,
  imports:[
    CommonModule,ReactiveFormsModule,MatTooltipModule,MatInputModule,FormlyModule
  ]
})
export class DateRangeWrapper extends FieldWrapper implements OnInit {
  getDisable(): any {
    return this.field.props?.disabled;
  }
  dateForm!: FormGroup;
  minToDate!: string;
  maxToDate!: string;
  constructor(private formBuilder: FormBuilder) {
    super();
  }

  ngOnInit() {
    this.dateForm = this.formBuilder.group({
      fromDate: [this.calculateFromDate(), Validators.required],
      toDate: [{ value: '', disabled: true }, [Validators.required, this.dateRangeValidator()]]
    });

    // Check if the formControl is an instance of FormGroup
    if (this.dateForm instanceof FormGroup) {
      // Create form controls for fromDate and toDate if they don't exist
      if (!this.formControl.get('fromDate')) {
        this.dateForm.addControl('fromDate', new FormControl());
      }
      if (!this.formControl.get('toDate')) {
        this.dateForm.addControl('toDate', new FormControl());
      }
    }

    this.dateForm.get('fromDate')?.valueChanges.subscribe(selectedDate => {
      if (selectedDate) {
        const fromDate = new Date(selectedDate);
        this.minToDate = this.calculateQuarterDate(fromDate, 0); // Minimum toDate
        this.maxToDate = this.calculateQuarterDate(fromDate, 6); // Maximum toDate, 6 days after the quarter date

        // Enable toDate field and reset its value
        const toDateControl = this.dateForm.get('toDate');
        this.updateFieldFormControlValue({ fromDate: selectedDate });
        toDateControl?.enable();
        toDateControl?.reset();
      }
    });

    this.dateForm.get('toDate')?.valueChanges.subscribe(selectedDate => {
      this.updateFieldFormControlValue({ toDate: selectedDate });
    });

  }

  onDateChange(event: { fromDate: string; toDate: string }) {
    // Update the form control values
    this.formControl.get('fromDate')?.setValue(event.fromDate);
    this.formControl.get('toDate')?.setValue(event.toDate);
  }

  private calculateFromDate(): string {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date.toISOString().split('T')[0];
  }

  private calculateQuarterDate(date: Date, additionalDays: number): string {
    date.setMonth(date.getMonth() + 3); // Quarterly period
    date.setDate(date.getDate() + additionalDays);
    return date.toISOString().split('T')[0];
  }

  private dateRangeValidator(): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      const selectedToDate = new Date(control.value);
      const minDate = new Date(this.minToDate);
      const maxDate = new Date(this.maxToDate);

      // Check if selected date falls within the allowed range
      const isWithinRange = selectedToDate >= minDate && selectedToDate <= maxDate;
      return isWithinRange ? null : { rangeError: true };
    };
  }

  private updateFieldFormControlValue(partialValue: { fromDate?: string; toDate?: string }) {
    // Merge the current value with the updated part
    if (this.field?.formControl) {
      const newValue = {
        ...this.field.formControl.value,
        ...partialValue,
      };
      this.field.formControl.setValue(newValue);
    }
  }
}
